import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Application, Job, JobStatus } from '@etnrlz-jobs/shared';
import { api } from '../lib/api';
import {
  AppStatusBadge,
  ErrorBox,
  FitScore,
  fmtDateTime,
  JobStatusBadge,
  PageHeader,
  RecommendationBadge,
  Spinner,
} from '../components/ui';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [apps, setApps] = useState<Application[] | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [j, a] = await Promise.all([
        api<{ job: Job }>(`/admin/jobs/${id}`),
        api<{ applications: Application[] }>(`/admin/jobs/${id}/applications`),
      ]);
      setJob(j.job);
      setApps(a.applications);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto houver avaliação de fit em andamento, atualiza a cada 5s.
  const hasProcessing = useMemo(
    () => (apps ?? []).some((a) => a.fitStatus === 'pending' || a.fitStatus === 'processing'),
    [apps],
  );
  useEffect(() => {
    if (!hasProcessing) return;
    const t = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(t);
  }, [hasProcessing, load]);

  const shareUrl = job ? `${location.origin}/v/${job.slug}` : '';

  const setStatus = async (status: JobStatus) => {
    setBusy(true);
    try {
      const { job: updated } = await api<{ job: Job }>(`/admin/jobs/${id}/status`, {
        method: 'POST',
        body: { status },
      });
      setJob(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm('Excluir esta vaga e todas as candidaturas? Essa ação não tem volta.')) return;
    setBusy(true);
    try {
      await api(`/admin/jobs/${id}`, { method: 'DELETE' });
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir');
      setBusy(false);
    }
  };

  const sorted = useMemo(
    () =>
      [...(apps ?? [])].sort((a, b) => (b.fit?.score ?? -1) - (a.fit?.score ?? -1)),
    [apps],
  );

  if (!job) {
    return error ? (
      <ErrorBox message={error} />
    ) : (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={job.title}
        subtitle={`Criada em ${fmtDateTime(job.createdAt)}`}
        actions={
          <>
            <Link to={`/vagas/${id}/editar`} className="btn-secondary">
              Editar
            </Link>
            {job.status !== 'open' ? (
              <button className="btn-primary" disabled={busy} onClick={() => setStatus('open')}>
                Publicar vaga
              </button>
            ) : (
              <button className="btn-secondary" disabled={busy} onClick={() => setStatus('closed')}>
                Encerrar vaga
              </button>
            )}
          </>
        }
      />
      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}

      <div className="mb-6 card flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <JobStatusBadge status={job.status} />
          <span className="text-sm text-slate-400">
            {job.applicationCount} {job.applicationCount === 1 ? 'candidatura' : 'candidaturas'}
          </span>
        </div>
        {job.status === 'open' ? (
          <div className="flex items-center gap-2">
            <code className="rounded bg-ink-950 px-3 py-1.5 text-xs text-brand-400">{shareUrl}</code>
            <button
              className="btn-secondary text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copiado!' : 'Copiar link'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-500">
            Publique a vaga para gerar o link de compartilhamento.
          </p>
        )}
      </div>

      <h2 className="mb-3 text-base font-semibold text-slate-100">
        Candidatos {hasProcessing && <Spinner className="ml-2 inline h-4 w-4 text-brand-400" />}
      </h2>
      {sorted.length === 0 && (
        <div className="card py-10 text-center text-sm text-slate-400">
          Nenhuma candidatura ainda. Compartilhe o link da vaga.
        </div>
      )}
      <div className="space-y-2">
        {sorted.map((app) => (
          <Link
            key={app.id}
            to={`/vagas/${id}/candidatos/${app.id}`}
            className="card flex items-center justify-between gap-4 py-4 hover:border-ink-500"
          >
            <div className="flex items-center gap-4">
              {app.fitStatus === 'done' && app.fit ? (
                <FitScore score={app.fit.score} />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-ink-600 text-slate-500">
                  {app.fitStatus === 'error' ? '!' : <Spinner />}
                </span>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-100">{app.name}</span>
                  <AppStatusBadge status={app.status} />
                  {app.fit && <RecommendationBadge rec={app.fit.recommendation} />}
                </div>
                <p className="mt-0.5 text-xs text-slate-400">
                  {app.email} · {fmtDateTime(app.createdAt)}
                </p>
              </div>
            </div>
            <span className="text-slate-500">→</span>
          </Link>
        ))}
      </div>

      <div className="mt-10 border-t border-ink-700 pt-6">
        <button className="btn-danger text-xs" disabled={busy} onClick={remove}>
          Excluir vaga
        </button>
      </div>
    </div>
  );
}
